import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/shared/lib/cn";

/**
 * Card — the workhorse container. Defaults to a 1-pixel hairline + soft
 * radius; `elevated` adds a Card-on-page shadow for emphasis (use sparingly).
 */
export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  elevated?: boolean;
  inset?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, elevated = false, inset = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "bg-[var(--bg-elevated)] border border-[var(--card-border)] rounded-[var(--radius-xl)]",
        elevated && "shadow-[var(--shadow-2)]",
        inset && "bg-[var(--bg-subtle)] border-transparent",
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = "Card";

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...p }, ref) => (
    <div ref={ref} className={cn("px-5 pt-5 pb-3", className)} {...p} />
  ),
);
CardHeader.displayName = "CardHeader";

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...p }, ref) => (
    <h3
      ref={ref}
      className={cn("text-[15px] font-semibold tracking-[-0.01em] text-[var(--fg)]", className)}
      {...p}
    />
  ),
);
CardTitle.displayName = "CardTitle";

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...p }, ref) => (
  <p ref={ref} className={cn("text-[13px] text-[var(--fg-subtle)] mt-1", className)} {...p} />
));
CardDescription.displayName = "CardDescription";

export const CardBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...p }, ref) => (
    <div ref={ref} className={cn("px-5 py-4", className)} {...p} />
  ),
);
CardBody.displayName = "CardBody";

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...p }, ref) => (
    <div
      ref={ref}
      className={cn(
        "px-5 py-3 border-t border-[var(--separator)] flex items-center justify-end gap-2",
        className,
      )}
      {...p}
    />
  ),
);
CardFooter.displayName = "CardFooter";
